import { useState, useMemo, useEffect, memo } from 'react'
import { cn } from '@/lib/utils'
import { ChevronRight, ChevronDown, File, Folder, FolderOpen } from 'lucide-react'
import type { FileNode } from '@/types/code'

interface FileTreeProps {
  data: FileNode[]
  selectedPath: string | null
  onSelect: (path: string) => void
  searchQuery?: string
}

/**
 * Filter tree nodes based on search query.
 * Returns filtered tree and set of paths that should be expanded.
 */
function filterTree(
  nodes: FileNode[],
  query: string
): { filtered: FileNode[]; expandedPaths: Set<string> } {
  const expandedPaths = new Set<string>()
  const lowerQuery = query.toLowerCase()

  function filterNode(node: FileNode): FileNode | null {
    const nameMatches = node.name.toLowerCase().includes(lowerQuery)

    if (node.type === 'file') {
      return nameMatches ? node : null
    }

    // For directories, check if any children match
    const filteredChildren: FileNode[] = []
    for (const child of node.children || []) {
      const filtered = filterNode(child)
      if (filtered) {
        filteredChildren.push(filtered)
      }
    }

    // Include directory if name matches OR has matching children
    if (nameMatches || filteredChildren.length > 0) {
      if (filteredChildren.length > 0) {
        expandedPaths.add(node.id)
      }
      return {
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : node.children,
      }
    }

    return null
  }

  const filtered: FileNode[] = []
  for (const node of nodes) {
    const result = filterNode(node)
    if (result) {
      filtered.push(result)
    }
  }

  return { filtered, expandedPaths }
}

function FileIcon({ name }: { name: string }) {
  const ext = name.split('.').pop()?.toLowerCase() || ''

  const iconColorClass = useMemo(() => {
    // TypeScript/JavaScript
    if (['ts', 'tsx'].includes(ext)) return 'text-blue-500'
    if (['js', 'jsx', 'mjs', 'cjs'].includes(ext)) return 'text-yellow-500'

    // Ruby
    if (['rb', 'erb', 'rake', 'gemspec'].includes(ext) || name === 'Gemfile' || name === 'Rakefile') return 'text-red-500'

    // Python
    if (['py', 'pyw'].includes(ext)) return 'text-green-500'

    // Data/Config
    if (['json', 'yaml', 'yml', 'toml'].includes(ext)) return 'text-amber-500'
    if (['env', 'ini', 'conf', 'cfg'].includes(ext)) return 'text-gray-500'

    // Web
    if (['html', 'htm'].includes(ext)) return 'text-orange-500'
    if (['css', 'scss', 'sass', 'less'].includes(ext)) return 'text-purple-500'

    // Markdown
    if (['md', 'mdx'].includes(ext)) return 'dark:text-gray-400 text-gray-500'

    // Shell
    if (['sh', 'bash', 'zsh'].includes(ext) || name === 'Procfile') return 'text-green-600'

    return 'dark:text-gray-400 text-gray-500'
  }, [ext, name])

  return <File className={cn('h-4 w-4 flex-shrink-0', iconColorClass)} />
}

const TreeItem = memo(function TreeItem({
  node,
  depth,
  selectedPath,
  onSelect,
  expandedPaths,
  searchQuery,
}: {
  node: FileNode
  depth: number
  selectedPath: string | null
  onSelect: (path: string) => void
  expandedPaths?: Set<string>
  searchQuery?: string
}) {
  const forceExpanded = expandedPaths?.has(node.id) ?? false
  const [isOpen, setIsOpen] = useState(forceExpanded)
  const isFolder = node.type === 'directory'
  const isSelected = selectedPath === node.id

  // Update open state when forceExpanded changes (search results)
  useEffect(() => {
    if (forceExpanded) {
      setIsOpen(true)
    }
  }, [forceExpanded])

  const handleClick = () => {
    if (isFolder) {
      setIsOpen(!isOpen)
    } else {
      onSelect(node.id)
    }
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 cursor-pointer rounded-sm text-sm select-none',
          'dark:text-gray-300 text-gray-700',
          'dark:hover:bg-white/10 hover:bg-gray-100 transition-colors',
          isSelected && 'dark:bg-white/20 dark:text-white bg-blue-100 text-blue-900'
        )}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={handleClick}
      >
        {/* Expand/Collapse Arrow for Folders */}
        {isFolder ? (
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5 dark:text-gray-500 text-gray-400" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 dark:text-gray-500 text-gray-400" />
            )}
          </span>
        ) : (
          <span className="w-4 h-4 flex-shrink-0" />
        )}

        {/* File/Folder Icon */}
        {isFolder ? (
          isOpen ? (
            <FolderOpen className="h-4 w-4 text-amber-500 flex-shrink-0" />
          ) : (
            <Folder className="h-4 w-4 text-amber-500 flex-shrink-0" />
          )
        ) : (
          <FileIcon name={node.name} />
        )}

        {/* Name */}
        <span className="truncate">{node.name}</span>
      </div>

      {/* Children (if folder is open) */}
      {isFolder && isOpen && node.children?.map((child) => (
        <TreeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          selectedPath={selectedPath}
          onSelect={onSelect}
          expandedPaths={expandedPaths}
          searchQuery={searchQuery}
        />
      ))}
    </div>
  )
})

export function FileTree({ data, selectedPath, onSelect, searchQuery }: FileTreeProps) {
  const { filtered, expandedPaths } = useMemo(() => {
    if (!searchQuery?.trim()) {
      return { filtered: data, expandedPaths: new Set<string>() }
    }
    return filterTree(data, searchQuery.trim())
  }, [data, searchQuery])

  return (
    <div className="py-1">
      {filtered.map((node) => (
        <TreeItem
          key={node.id}
          node={node}
          depth={0}
          selectedPath={selectedPath}
          onSelect={onSelect}
          expandedPaths={expandedPaths}
          searchQuery={searchQuery}
        />
      ))}
    </div>
  )
}
